import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Seo } from "@/seo/Seo";
import { toast } from "sonner";
import {
  ArrowRight, Loader2, Check, ShieldCheck, FileText, PenLine, Lock, ChevronDown,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { packageAccess, packageAccept } from "@/lib/api";

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); }
  catch { return d; }
};

function DocCard({ doc }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`agreement-doc-${doc.document_type}`} className="border border-[#1f1f1f] rounded-2xl bg-[#0b0b0c] overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} data-testid={`agreement-doc-toggle-${doc.document_type}`}
        className="w-full flex items-start justify-between gap-4 px-5 py-4 text-left hover:bg-white/[0.02] transition-colors">
        <div className="flex items-start gap-3">
          <FileText className="h-4 w-4 text-cyan-400 mt-1 shrink-0" />
          <div>
            <div className="text-white font-medium text-sm">{doc.title}</div>
            <div className="mt-1 text-zinc-500 text-xs mono-label flex flex-wrap gap-x-3 gap-y-1">
              <span>DOC {doc.document_number}</span>
              <span>v{doc.version}</span>
              <span>EFF {fmtDate(doc.effective_date)}</span>
              {doc.requires_signature
                ? <span className="text-amber-300 inline-flex items-center gap-1"><PenLine className="h-3 w-3" /> SIGNATURE REQUIRED</span>
                : <span className="text-emerald-300">ACKNOWLEDGEMENT</span>}
            </div>
            <div className="mt-1 text-zinc-600 text-[11px]">{doc.required_reason}</div>
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 text-zinc-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5">
          <div data-testid={`agreement-doc-content-${doc.document_type}`}
            className="max-h-[320px] overflow-y-auto rounded-lg border border-[#171717] bg-black/40 p-4 text-zinc-300 text-[13px] leading-relaxed whitespace-pre-wrap">
            {doc.content || "Content unavailable."}
          </div>
          <div className="mt-2 text-zinc-600 text-[10px] mono-label break-all">HASH {doc.sha256_hash}</div>
        </div>
      )}
    </div>
  );
}

export default function AgreementPackage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [pkg, setPkg] = useState(null);
  const [ack, setAck] = useState(false);
  const [name, setName] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [done, setDone] = useState(null);

  const open = async () => {
    if (!/\S+@\S+\.\S+/.test(email)) return toast.error("Enter the email associated with this engagement.");
    setBusy(true);
    try {
      const d = await packageAccess(token, email.trim());
      setPkg(d);
      if (d.acceptance) setDone(d.acceptance);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "This agreement package is not available.");
    } finally { setBusy(false); }
  };

  const accept = async () => {
    if (!ack) return toast.error("Please acknowledge the agreement package first.");
    setAccepting(true);
    try {
      const res = await packageAccept(token, email.trim(), name.trim() || undefined);
      setDone(res);
      toast.success("Agreement package accepted.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object") {
        if (detail.error === "signature_required") toast.error("This package requires signature — a signature step will be provided.");
        else if (detail.error === "package_integrity_error") toast.error("A document changed since presentation. Acceptance blocked.");
        else toast.error(detail.message || "Could not accept.");
      } else {
        toast.error(detail || "Could not accept.");
      }
    } finally { setAccepting(false); }
  };

  const Header = () => (
    <header className="border-b border-[#141414] sticky top-0 z-40 bg-black/80 backdrop-blur-xl">
      <Seo noindex path="/agreement" title="Agreement Package · AIROTECH" />
      <div className="max-w-4xl mx-auto px-6 h-[72px] flex items-center justify-between">
        <button onClick={() => navigate("/")}><img src="/airotech-mark.png" alt="AIROTECH" className="h-8 w-auto" /></button>
        <div className="mono-label text-cyan-400 flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> AGREEMENT PACKAGE</div>
      </div>
    </header>
  );

  // Gate
  if (!pkg) return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <div className="max-w-xl mx-auto px-6 pt-20">
        <div className="mono-label text-cyan-400 mb-6">AIROTECH LEGAL</div>
        <h1 className="font-heading font-extrabold tracking-tighter text-3xl md:text-5xl">Review your agreement package.</h1>
        <p className="mt-5 text-zinc-400">Confirm the email associated with your engagement to view and accept the documents presented to you.</p>
        <div className="mt-8 space-y-3">
          <Label className="mono-label text-zinc-400">Your email</Label>
          <Input data-testid="agreement-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && open()}
            className="bg-[#0b0b0c] border-[#1f1f1f] text-white h-12 focus-visible:ring-cyan-400/40 focus-visible:border-cyan-400/60" placeholder="you@company.com" />
        </div>
        <button data-testid="agreement-open" onClick={open} disabled={busy}
          className="mt-6 inline-flex items-center gap-2 bg-white text-black font-medium px-7 py-4 rounded-full mono-label hover:bg-cyan-400 transition-colors duration-200 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>View Package <ArrowRight className="h-4 w-4" /></>}
        </button>
      </div>
    </div>
  );

  const accepted = done || pkg.status === "ACCEPTED";

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mono-label text-cyan-400 mb-3">{pkg.package_name}</div>
        <h1 data-testid="agreement-title" className="font-heading font-extrabold tracking-tighter text-3xl md:text-4xl">
          {accepted ? "Agreement accepted." : "Review & accept your agreement."}
        </h1>
        <p className="mt-4 text-zinc-400 max-w-2xl">
          {accepted
            ? "You have accepted the exact document versions below. This record is immutable and cryptographically bound to each document's hash."
            : "These are the exact document versions presented to you. Expand any document to read it in full, then acknowledge and accept."}
        </p>
        <div className="mt-2 text-zinc-600 text-[10px] mono-label break-all">PACKAGE HASH {pkg.package_hash}</div>

        {accepted && (
          <div data-testid="agreement-accepted-banner" className="mt-6 rounded-2xl border border-emerald-400/30 bg-emerald-400/10 px-5 py-4 flex items-start gap-3">
            <Check className="h-5 w-5 text-emerald-300 mt-0.5" />
            <div className="text-sm">
              <div className="text-emerald-200 font-medium">Accepted on {fmtDate((done && done.accepted_at) || pkg.accepted_at)}</div>
              <div className="text-emerald-300/70 text-xs mt-1">Acceptance ID: {(done && done.acceptance_id) || (pkg.acceptance && pkg.acceptance.id)}</div>
            </div>
          </div>
        )}

        <div className="mt-8 space-y-3">
          {pkg.documents.map((d) => <DocCard key={d.document_id} doc={d} />)}
        </div>

        {!accepted && (
          <div className="mt-8 border-t border-[#141414] pt-8">
            {pkg.requires_signature && (
              <div className="mb-5 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-amber-200 text-sm flex items-start gap-2">
                <PenLine className="h-4 w-4 mt-0.5 shrink-0" />
                <span>This package includes documents that require a signature. After acknowledging, you'll be routed into the signature workflow to execute those documents.</span>
              </div>
            )}
            <div className="space-y-3 max-w-md">
              <Label className="mono-label text-zinc-400">Your full legal name (for the record)</Label>
              <Input data-testid="agreement-name" value={name} onChange={(e) => setName(e.target.value)}
                className="bg-[#0b0b0c] border-[#1f1f1f] text-white h-12" placeholder={pkg.customer_name} />
            </div>
            <label className="mt-5 flex items-start gap-3 cursor-pointer max-w-2xl">
              <input data-testid="agreement-ack" type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
                className="mt-1 h-4 w-4 accent-cyan-400" />
              <span className="text-zinc-300 text-sm">
                I acknowledge that I have reviewed the {pkg.documents.length} document(s) above, at the exact versions and hashes shown, and I agree to accept this agreement package.
              </span>
            </label>
            <button data-testid="agreement-accept" onClick={accept} disabled={accepting || !ack}
              className="mt-6 inline-flex items-center gap-2 bg-white text-black font-medium px-7 py-4 rounded-full mono-label hover:bg-cyan-400 transition-colors duration-200 disabled:opacity-50">
              {accepting ? <Loader2 className="h-4 w-4 animate-spin" />
                : pkg.requires_signature ? <><Lock className="h-4 w-4" /> Acknowledge & Continue</>
                : <><Check className="h-4 w-4" /> Accept Agreement Package</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
